/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { meetingsAPI, workingGroupsAPI } from '../lib/api';
import { formatDate, MEETING_TYPES, TARGET_AUDIENCES, KENT_KONSEYI_ANA_KADEME, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import EmailNotifyToggle from '../components/EmailNotifyToggle';
import {
  Calendar,
  Plus, FileDown,
  Eye,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  Users,
  Loader2,
  Trash2,
  Shield,
  Lock,
  Building,
  Search,
  X,
  Filter,
  CalendarDays,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Pencil,
} from 'lucide-react';

const MeetingsListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageTitle('Toplantilar');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const limit = 10;

  const isAdmin = ['admin', 'genel_sekreter', 'meclis_baskani', 'muhtar'].includes(user?.role);
  const canApprove = ['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'].includes(user?.role);

  const handleApprove = async (id, e) => {
    e.stopPropagation();
    try {
      await meetingsAPI.approve(id);
      toast.success('Toplanti yayinlandi');
      loadMeetings();
    } catch { toast.error('Onaylama basarisiz'); }
  };
  const handleReject = async (id, e) => {
    e.stopPropagation();
    const reason = window.prompt('Reddetme sebebi (opsiyonel):') || '';
    try {
      await meetingsAPI.reject(id, reason);
      toast.success('Toplanti reddedildi');
      loadMeetings();
    } catch { toast.error('Reddetme basarisiz'); }
  };
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu toplantıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;
    try {
      await meetingsAPI.delete(id);
      toast.success('Toplantı silindi');
      loadMeetings();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Silme başarısız');
    }
  };

  const hasActiveFilters = searchQuery || statusFilter || audienceFilter || dateFrom || dateTo;

  useEffect(() => {
    loadMeetings();
  }, [page, typeFilter, statusFilter, audienceFilter, dateFrom, dateTo]);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * limit,
        limit,
        meeting_type: typeFilter || undefined,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        target_audience: audienceFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      };
      const response = await meetingsAPI.getAll(params);
      setMeetings(response.data.meetings);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Toplantilar yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadMeetings();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('');
    setStatusFilter('');
    setAudienceFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const getTypeBadge = (type) => {
    const typeInfo = MEETING_TYPES[type] || { label: type, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return (
      <Badge className={`${colorClasses[typeInfo.color]} border`}>
        {typeInfo.label}
      </Badge>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="meetings-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Toplantilar</h1>
          <p className="text-slate-500 text-sm">Kent Konseyi toplantilarini goruntuleyebilirsiniz</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { const a=document.createElement('a'); a.href=`${process.env.REACT_APP_BACKEND_URL}/api/meetings/export/excel`; a.click(); }} data-testid="export-meetings-btn">
              <FileDown className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button onClick={() => navigate('/panel/toplantilar/yeni')} data-testid="new-meeting-btn" className="shadow-sm">
              <Plus className="w-4 h-4 mr-2" /> Yeni Toplanti
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {/* Arama + Filtre Toggle */}
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative" data-testid="mtg-search-form">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Baslik, aciklama veya konum ara..."
              className="pl-9 pr-9 h-9"
              data-testid="mtg-search-input"
            />
            {searchQuery && (
              <button type="button" onClick={() => { setSearchQuery(''); setPage(1); setTimeout(loadMeetings, 0); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
          <Button variant={showFilters ? "default" : "outline"} size="sm" onClick={() => setShowFilters(!showFilters)}
            className="h-9 gap-1.5" data-testid="mtg-toggle-filters-btn">
            <Filter className="w-3.5 h-3.5" />
            Filtrele
            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-blue-400 ml-1" />}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-slate-500 hover:text-red-600" data-testid="mtg-clear-filters-btn">
              <X className="w-3.5 h-3.5 mr-1" /> Temizle
            </Button>
          )}
        </div>

        {/* Tur Pill'leri */}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setTypeFilter(''); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${!typeFilter ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid="filter-all">Tumu</button>
          {Object.entries(MEETING_TYPES).map(([key, val]) => {
            const active = typeFilter === key;
            const typeColors = {
              genel_kurul: active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200',
              yurutme_kurulu: active ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-orange-700 border-orange-200',
              meclis: active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200',
              calisma_grubu: active ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-700 border-purple-200',
              diger: active ? 'bg-slate-600 text-white border-slate-600' : 'bg-white text-slate-600 border-slate-200',
            };
            return (
              <button key={key} onClick={() => { setTypeFilter(key); setPage(1); }}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${typeColors[key] || (active ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200')}`}
                data-testid={`filter-${key}`}>{val.label}</button>
            );
          })}
          <span className="ml-auto text-xs text-slate-400">{total} toplanti</span>
        </div>

        {/* Genisletilmis Filtreler */}
        {showFilters && (
          <Card className="shadow-sm border-slate-200" data-testid="mtg-advanced-filters">
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Durum
                  </Label>
                  <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="h-9" data-testid="mtg-status-filter">
                      <SelectValue placeholder="Tum durumlar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tum Durumlar</SelectItem>
                      <SelectItem value="planlanmis">Planlanmis</SelectItem>
                      <SelectItem value="tamamlandi">Tamamlandi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Hedef Kitle
                  </Label>
                  <Select value={audienceFilter} onValueChange={(v) => { setAudienceFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="h-9" data-testid="mtg-audience-filter">
                      <SelectValue placeholder="Tum hedef kitleler" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tum Hedef Kitleler</SelectItem>
                      {TARGET_AUDIENCES.map(a => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" /> Baslangic Tarihi
                  </Label>
                  <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                    className="h-9" data-testid="mtg-date-from" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" /> Bitis Tarihi
                  </Label>
                  <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                    className="h-9" data-testid="mtg-date-to" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : meetings.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {meetings.map((m) => {
              const isPast = new Date(m.date) < new Date();
              const typeStrip = {
                genel_kurul: 'from-emerald-500 to-emerald-600',
                meclis_toplantisi: 'from-blue-500 to-blue-600',
                calisma_grubu: 'from-purple-500 to-purple-600',
              };
              return (
                <div key={m.id} className="group bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg transition-all overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/panel/toplantilar/${m.id}`)} data-testid={`meeting-card-${m.id}`}>
                  <div className={`h-1.5 bg-gradient-to-r ${typeStrip[m.meeting_type] || 'from-slate-400 to-slate-500'}`} />
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      {getTypeBadge(m.meeting_type)}
                      <div className="flex items-center gap-1.5">
                        {m.approval_status === 'onay_bekliyor' && (
                          <Badge className="text-xs h-5 px-2 bg-amber-100 text-amber-800 border border-amber-300" data-testid={`approval-pending-${m.id}`}>
                            <Clock className="w-3 h-3 mr-1" /> Onay Bekliyor
                          </Badge>
                        )}
                        {m.approval_status === 'reddedildi' && (
                          <Badge className="text-xs h-5 px-2 bg-red-50 text-red-700 border border-red-200">Reddedildi</Badge>
                        )}
                        {m.mahalle_name && (
                          <Badge className="text-xs h-5 px-2 bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`mahalle-badge-${m.id}`}>
                            <MapPin className="w-3 h-3 mr-1" /> {m.mahalle_name}
                          </Badge>
                        )}
                        <Badge variant="outline" className={`text-xs ${isPast ? 'border-slate-200 text-slate-400' : 'border-emerald-200 text-emerald-700 bg-emerald-50'}`}>
                          {m.status === 'planlanmis' ? 'Planlanmis' : 'Tamamlandi'}
                        </Badge>
                      </div>
                    </div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors mb-3 line-clamp-2">{m.title}</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-sm">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
                          <Calendar className="w-4 h-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="text-slate-900 font-medium text-sm">{formatDate(m.date)}</p>
                          <p className="text-xs text-slate-400">{m.time}</p>
                        </div>
                      </div>
                      {m.location && (
                        <div className="flex items-center gap-3 text-sm">
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
                            <MapPin className="w-4 h-4 text-slate-500" />
                          </div>
                          <p className="text-sm text-slate-600 truncate">{m.location}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-sm">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100">
                          <Users className="w-4 h-4 text-slate-500" />
                        </div>
                        <p className="text-sm text-slate-600 truncate">
                          {m.target_audience?.map(a => TARGET_AUDIENCES.find(t => t.value === a)?.label || a).join(', ')}
                        </p>
                      </div>
                    </div>
                    {m.target_group_ids?.length > 0 && (
                      <div className="mt-3 flex items-center gap-1.5">
                        <Lock className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-xs font-medium text-amber-600">Grup Kisitli</span>
                      </div>
                    )}
                    {canApprove && m.approval_status === 'onay_bekliyor' && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/panel/toplantilar/${m.id}/duzenle`); }} data-testid={`edit-meeting-${m.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Duzenle
                        </Button>
                        <Button size="sm" className="h-7 text-xs flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={(e) => handleApprove(m.id, e)} data-testid={`approve-meeting-${m.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Onayla
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1 border-red-200 text-red-700 hover:bg-red-50" onClick={(e) => handleReject(m.id, e)} data-testid={`reject-meeting-${m.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reddet
                        </Button>
                      </div>
                    )}
                    {!canApprove && m.approval_status === 'onay_bekliyor' && m.created_by === user?.id && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={(e) => { e.stopPropagation(); navigate(`/panel/toplantilar/${m.id}/duzenle`); }} data-testid={`edit-meeting-${m.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Duzenle
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={(e) => handleDelete(m.id, e)} data-testid={`delete-meeting-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                        </Button>
                      </div>
                    )}
                    {canApprove && m.approval_status !== 'onay_bekliyor' && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={(e) => { e.stopPropagation(); navigate(`/panel/toplantilar/${m.id}/duzenle`); }} data-testid={`edit-meeting-${m.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Duzenle
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={(e) => handleDelete(m.id, e)} data-testid={`delete-meeting-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-slate-400">Sayfa {page}/{totalPages}</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pNum = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  if (pNum > totalPages) return null;
                  return <Button key={pNum} variant={pNum === page ? "default" : "outline"} size="sm" onClick={() => setPage(pNum)} className="h-8 w-8 p-0 text-xs">{pNum}</Button>;
                })}
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4"><Calendar className="w-7 h-7 text-slate-300" /></div>
            <h3 className="text-base font-semibold text-slate-900">Henuz toplanti yok</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-xs">{isAdmin ? 'Ilk toplantiyi olusturmak icin butona tiklayin' : 'Henuz toplanti planlanmadi'}</p>
            {isAdmin && <Button onClick={() => navigate('/panel/toplantilar/yeni')} className="mt-4" size="sm"><Plus className="w-4 h-4 mr-2" /> Yeni Toplanti</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const NewMeetingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { meetingId } = useParams();
  const isEditMode = Boolean(meetingId);
  usePageTitle(isEditMode ? 'Toplantı Düzenle' : 'Yeni Toplanti');
  const [loading, setLoading] = useState(false);
  const [loadingMeeting, setLoadingMeeting] = useState(isEditMode);
  const [meclisler, setMeclisler] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    meeting_type: '',
    target_audience: [],
    target_group_ids: [],
    agenda: [''],
  });
  const [sendEmail, setSendEmail] = useState(false);
  const [emailAudiences, setEmailAudiences] = useState([]);

  // Edit mode: mevcut toplantiyi yukle
  useEffect(() => {
    if (!isEditMode) {
      setLoadingMeeting(false);
      return;
    }
    setLoadingMeeting(true);
    (async () => {
      try {
        const r = await meetingsAPI.getById(meetingId);
        const m = r.data;
        setFormData({
          title: m.title || '',
          description: m.description || '',
          date: m.date ? String(m.date).slice(0, 10) : '',
          time: m.time || '',
          location: m.location || '',
          meeting_type: m.meeting_type || '',
          target_audience: m.target_audience || [],
          target_group_ids: m.target_group_ids || [],
          agenda: (m.agenda && m.agenda.length > 0) ? m.agenda : [''],
        });
      } catch {
        toast.error('Toplantı yüklenemedi');
        navigate('/panel/toplantilar');
      } finally {
        setLoadingMeeting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, isEditMode]);

  const showGroupSelector = formData.target_audience.includes('calisma_gruplari') || formData.target_audience.includes('meclisler');

  const isAnaKademeSelected = KENT_KONSEYI_ANA_KADEME.every(v => formData.target_audience.includes(v));

  const toggleAnaKademe = () => {
    if (isAnaKademeSelected) {
      setFormData({
        ...formData,
        target_audience: formData.target_audience.filter(a => !KENT_KONSEYI_ANA_KADEME.includes(a)),
      });
    } else {
      setFormData({
        ...formData,
        target_audience: [...new Set([...formData.target_audience, ...KENT_KONSEYI_ANA_KADEME])],
      });
    }
  };

  useEffect(() => {
    if (showGroupSelector && meclisler.length === 0) {
      loadMeclislerAndGroups();
    }
  }, [showGroupSelector]);

  const loadMeclislerAndGroups = async () => {
    setLoadingGroups(true);
    try {
      const resp = await workingGroupsAPI.getMeclisler();
      setMeclisler(resp.data);
      // Load all working groups + yurutme kurulu for each meclis
      const groups = [];
      for (const m of resp.data) {
        const detail = await workingGroupsAPI.getMeclisDetail(m.id);
        for (const wg of (detail.data.working_groups || [])) {
          groups.push({ ...wg, kind: 'calisma_grubu', meclis_name: m.name, meclis_id: m.id });
        }
        for (const yk of (detail.data.yurutme_kurulu_list || [])) {
          groups.push({ ...yk, kind: 'yurutme_kurulu', meclis_name: m.name, meclis_id: m.id });
        }
      }
      setAllGroups(groups);
    } catch (e) {
      console.error('Failed to load groups:', e);
    } finally {
      setLoadingGroups(false);
    }
  };

  const toggleGroupId = (id) => {
    const current = formData.target_group_ids;
    if (current.includes(id)) {
      setFormData({ ...formData, target_group_ids: current.filter(g => g !== id) });
    } else {
      setFormData({ ...formData, target_group_ids: [...current, id] });
    }
  };

  // Sadece meclisi toggle et (alt YK ve CG'leri etkilemez - kullanici tam esnek secim yapabilsin)
  const toggleMeclisOnly = (meclisId) => {
    const current = formData.target_group_ids;
    if (current.includes(meclisId)) {
      setFormData({ ...formData, target_group_ids: current.filter(id => id !== meclisId) });
    } else {
      setFormData({ ...formData, target_group_ids: [...current, meclisId] });
    }
  };

  // Meclis + alti TUM YK ve CG'leri tek seferde toggle et (kolaylik secimi)
  const toggleAllInMeclis = (meclisId) => {
    const subGroups = allGroups.filter(g => g.meclis_id === meclisId).map(g => g.id);
    const allIds = [meclisId, ...subGroups];
    const current = formData.target_group_ids;
    const allSelected = allIds.every(id => current.includes(id));
    if (allSelected) {
      setFormData({ ...formData, target_group_ids: current.filter(id => !allIds.includes(id)) });
    } else {
      setFormData({ ...formData, target_group_ids: [...new Set([...current, ...allIds])] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date || !formData.time || !formData.meeting_type) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }
    
    if (formData.target_audience.length === 0) {
      toast.error('En az bir hedef kitle seçin');
      return;
    }

    // Edit mode: PUT ile guncelle
    if (isEditMode) {
      setLoading(true);
      try {
        await meetingsAPI.update(meetingId, {
          ...formData,
          agenda: formData.agenda.filter(a => a.trim()),
          target_group_ids: formData.target_group_ids.length > 0 ? formData.target_group_ids : null,
          send_notification_email: false,
          email_target_audiences: null,
        });
        toast.success('Toplantı güncellendi');
        navigate('/panel/toplantilar');
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Toplantı güncellenemedi');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    try {
      const isMuhtar = user?.role === 'muhtar';
      const data = {
        ...formData,
        agenda: formData.agenda.filter(a => a.trim()),
        target_group_ids: formData.target_group_ids.length > 0 ? formData.target_group_ids : null,
        send_notification_email: isMuhtar ? false : sendEmail,
        email_target_audiences: (!isMuhtar && sendEmail) ? (emailAudiences.length > 0 ? emailAudiences : formData.target_audience) : null,
      };
      const response = await meetingsAPI.create(data);
      toast.success('Toplantı oluşturuldu');
      navigate(`/panel/toplantilar/${response.data.id}`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Toplantı oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const toggleAudience = (value) => {
    if (formData.target_audience.includes(value)) {
      setFormData({
        ...formData,
        target_audience: formData.target_audience.filter(a => a !== value),
      });
    } else {
      setFormData({
        ...formData,
        target_audience: [...formData.target_audience, value],
      });
    }
  };

  const addAgendaItem = () => {
    setFormData({
      ...formData,
      agenda: [...formData.agenda, ''],
    });
  };

  const updateAgendaItem = (index, value) => {
    const updated = [...formData.agenda];
    updated[index] = value;
    setFormData({ ...formData, agenda: updated });
  };

  const removeAgendaItem = (index) => {
    setFormData({
      ...formData,
      agenda: formData.agenda.filter((_, i) => i !== index),
    });
  };

  if (isEditMode && loadingMeeting) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-meeting-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/toplantilar')} className="text-slate-600 hover:text-slate-900">
          <ChevronLeft className="w-4 h-4 mr-1" /> Toplantilara Don
        </Button>
      </div>

      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-blue-800 via-blue-900 to-blue-800 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{isEditMode ? 'Toplantı Düzenle' : 'Yeni Toplanti Olustur'}</h1>
              <p className="text-sm text-white/50">Toplanti detaylarini doldurun</p>
            </div>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol - Ana Form */}
          <div className="lg:col-span-2 space-y-5">
            {/* Temel Bilgiler */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                  Temel Bilgiler
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title" className="text-sm font-medium">Toplanti Basligi *</Label>
                  <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Toplanti basligi" required data-testid="meeting-title-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="description" className="text-sm font-medium">Aciklama</Label>
                  <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Toplanti hakkinda kisa aciklama" rows={4} className="mt-1.5" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date" className="text-sm font-medium">Tarih *</Label>
                    <Input id="date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required data-testid="meeting-date-input" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="time" className="text-sm font-medium">Saat *</Label>
                    <Input id="time" type="time" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required data-testid="meeting-time-input" className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="location" className="text-sm font-medium">Konum</Label>
                  <Input id="location" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Toplanti yeri" className="mt-1.5" />
                </div>
              </CardContent>
            </Card>

            {/* Hedef Kitle */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                  Hedef Kitle
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Toplantiya katilacak gruplari secin</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {TARGET_AUDIENCES.map((audience) => {
                      const checked = formData.target_audience.includes(audience.value);
                      return (
                        <button key={audience.value} type="button" onClick={() => toggleAudience(audience.value)}
                          data-testid={`audience-${audience.value}`}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm text-left transition-all ${checked ? 'border-blue-500 bg-blue-50 text-blue-800 font-medium' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
                            {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          {audience.label}
                        </button>
                      );
                    })}
                  </div>
                  <button type="button" onClick={toggleAnaKademe}
                    data-testid="audience-ana-kademe-btn"
                    className={`w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-semibold transition-all ${isAnaKademeSelected ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-slate-300 bg-slate-50 text-slate-600 hover:border-indigo-400 hover:bg-indigo-50/50'}`}>
                    <Building className={`w-4 h-4 ${isAnaKademeSelected ? 'text-indigo-600' : 'text-slate-400'}`} />
                    Kent Konseyi Ana Kademe
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isAnaKademeSelected ? 'bg-indigo-200 text-indigo-800' : 'bg-slate-200 text-slate-500'}`}>
                      {isAnaKademeSelected ? 'Secildi' : 'Toplu Sec'}
                    </span>
                  </button>
                  {isAnaKademeSelected && (
                    <p className="text-xs text-indigo-600 text-center">Yonetim Kurulu + Genel Sekreter + Kent Konseyi Baskani secildi</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Grup Hedefleme */}
            {showGroupSelector && (
              <Card className="shadow-sm border-amber-200">
                <CardHeader className="pb-3 bg-amber-50/50">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-800">
                    <Shield className="w-5 h-5 text-amber-600" /> Meclis / Calisma Grubu Hedefleme
                  </CardTitle>
                  <p className="text-xs text-amber-700 mt-1">Sectiginiz gruplara yalnizca onayli uyeler erisebilir.</p>
                </CardHeader>
                <CardContent className="pt-4" data-testid="group-selector-section">
                  {loadingGroups ? (
                    <div className="flex items-center gap-2 py-6 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                      <span className="text-sm text-amber-700">Gruplar yukleniyor...</span>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {meclisler.map((meclis) => {
                        const subGroups = allGroups.filter(g => g.meclis_id === meclis.id);
                        const mGroups = subGroups.filter(g => g.kind === 'calisma_grubu');
                        const mYKs = subGroups.filter(g => g.kind === 'yurutme_kurulu');
                        const subIds = subGroups.map(g => g.id);
                        const meclisChecked = formData.target_group_ids.includes(meclis.id);
                        const allChecked = [meclis.id, ...subIds].every(id => formData.target_group_ids.includes(id));
                        return (
                          <div key={meclis.id} className="border border-amber-100 rounded-lg bg-white p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox id={`meclis-${meclis.id}`} checked={meclisChecked} onCheckedChange={() => toggleMeclisOnly(meclis.id)} data-testid={`meclis-checkbox-${meclis.id}`} />
                                <label htmlFor={`meclis-${meclis.id}`} className="text-sm font-bold text-amber-900 cursor-pointer">{meclis.name}</label>
                              </div>
                              {subGroups.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleAllInMeclis(meclis.id)}
                                  className="text-[10px] text-amber-700 hover:text-amber-900 hover:underline px-2 py-0.5"
                                  data-testid={`meclis-toggle-all-${meclis.id}`}
                                >
                                  {allChecked ? 'Tumunu temizle' : 'Tumunu sec (Meclis + YK + CG)'}
                                </button>
                              )}
                            </div>
                            {mYKs.length > 0 && (
                              <div className="ml-6 mb-2 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Yurutme Kurulu</p>
                                {mYKs.map(yk => (
                                  <div key={yk.id} className="flex items-center space-x-2">
                                    <Checkbox id={`group-${yk.id}`} checked={formData.target_group_ids.includes(yk.id)} onCheckedChange={() => toggleGroupId(yk.id)} data-testid={`group-checkbox-${yk.id}`} />
                                    <label htmlFor={`group-${yk.id}`} className="text-xs text-emerald-800 cursor-pointer">{yk.name}</label>
                                  </div>
                                ))}
                              </div>
                            )}
                            {mGroups.length > 0 && (
                              <div className="ml-6 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Calisma Gruplari</p>
                                {mGroups.map(g => (
                                  <div key={g.id} className="flex items-center space-x-2">
                                    <Checkbox id={`group-${g.id}`} checked={formData.target_group_ids.includes(g.id)} onCheckedChange={() => toggleGroupId(g.id)} data-testid={`group-checkbox-${g.id}`} />
                                    <label htmlFor={`group-${g.id}`} className="text-xs text-slate-700 cursor-pointer">{g.name}</label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {formData.target_group_ids.length > 0 && (
                    <p className="text-xs text-amber-700 mt-2 font-medium">{formData.target_group_ids.length} grup/meclis secildi</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Gundem Maddeleri */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                    Gundem Maddeleri
                  </CardTitle>
                  <Button type="button" variant="outline" size="sm" onClick={addAgendaItem} className="h-8">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ekle
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {formData.agenda.map((item, index) => (
                    <div key={`agenda-input-${index}`} className="flex items-center gap-2">
                      <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">{index + 1}</span>
                      <Input value={item} onChange={(e) => updateAgendaItem(index, e.target.value)} placeholder={`Gundem maddesi ${index + 1}`} className="flex-1" />
                      {formData.agenda.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeAgendaItem(index)} className="h-9 w-9 flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sag - Ayarlar Sidebar */}
          <div className="space-y-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Toplanti Turu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(MEETING_TYPES).map(([key, val]) => {
                    const selected = formData.meeting_type === key;
                    const typeColors = {
                      genel_kurul: selected ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-600',
                      meclis_toplantisi: selected ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-600',
                      calisma_grubu: selected ? 'border-purple-500 bg-purple-50 text-purple-800' : 'border-slate-200 text-slate-600',
                    };
                    const dotColors = {
                      genel_kurul: selected ? 'bg-emerald-500' : 'bg-slate-300',
                      meclis_toplantisi: selected ? 'bg-blue-500' : 'bg-slate-300',
                      calisma_grubu: selected ? 'bg-purple-500' : 'bg-slate-300',
                    };
                    return (
                      <button key={key} type="button" onClick={() => setFormData({ ...formData, meeting_type: key })}
                        className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg border text-sm text-left transition-all ${typeColors[key] || (selected ? 'border-blue-500 bg-blue-50' : 'border-slate-200')}`}
                        data-testid={`meeting-type-${key}`}>
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColors[key] || 'bg-slate-300'}`} />
                        <span className="font-medium">{val.label}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Ozet */}
            <Card className="shadow-sm border-dashed">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-500">Ozet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{formData.date || '-- Tarih --'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{formData.time || '-- Saat --'}</span>
                </div>
                {formData.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600 truncate">{formData.location}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.target_audience.map(a => (
                    <Badge key={a} variant="outline" className="text-xs h-5 px-1.5">{TARGET_AUDIENCES.find(t => t.value === a)?.label || a}</Badge>
                  ))}
                </div>
                <div className="text-xs text-slate-400">{formData.agenda.filter(a => a.trim()).length} gundem maddesi</div>
              </CardContent>
            </Card>

            {/* E-posta Bildirim - Muhtarlara kesinlikle gosterme */}
            {user?.role !== 'muhtar' && (
              <EmailNotifyToggle
                sendEmail={sendEmail} setSendEmail={setSendEmail}
                emailAudiences={emailAudiences} setEmailAudiences={setEmailAudiences}
                defaultAudiences={formData.target_audience}
              />
            )}

            {/* Actions */}
            <div className="space-y-2">
              <Button type="submit" disabled={loading} className="w-full shadow-sm" data-testid="submit-meeting-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                {isEditMode ? 'Değişiklikleri Kaydet' : 'Toplanti Olustur'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/panel/toplantilar')} className="w-full">
                Iptal
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export { MeetingsListPage, NewMeetingPage };
